// src/lib/constants/stream.ts

import type { SortDirection } from '@/lib/types/common';
import type { StreamSortKey } from '@/lib/types/Stream';

export const STREAM_TYPES = ['push', 'pull', 'simulatedlive'] as const;
export type StreamType = typeof STREAM_TYPES[number];

export const STREAM_TYPE_LABELS: Record<StreamType, string> = {
  push: 'Live Push',
  pull: 'Live Pull',
  simulatedlive: 'Simulated Live',
} as const;

export const STREAM_RECORD_MODES = ['manual', 'automatic', 'schedule'] as const;
export type StreamRecordMode = typeof STREAM_RECORD_MODES[number];

/** Only manual and automatic shown in create UI; schedule is edit-only */
export const STREAM_CREATE_RECORD_MODES = ['manual', 'automatic'] as const;

export const STREAM_ADAPTIVE_MODES = ['none', 'publisher', 'transcode_event', 'transcode_continuous'] as const;
export type StreamAdaptiveMode = typeof STREAM_ADAPTIVE_MODES[number];

export const STREAM_OOS_MODES = ['offline', 'online'] as const;
export type StreamOosMode = typeof STREAM_OOS_MODES[number];

export const STREAM_OFFLINE_DISPLAY_MODES = ['default_message', 'custom_message', 'offline_poster', 'media', 'list'] as const;
export type StreamOfflineDisplayMode = typeof STREAM_OFFLINE_DISPLAY_MODES[number];

export const DVR_ARCHIVE_STRATEGY_TYPES = ['append', 'delete'] as const;
export type DvrArchiveStrategyType = typeof DVR_ARCHIVE_STRATEGY_TYPES[number];

export const STREAM_OUTPUT_SOURCE_TYPES = ['publisher', 'transcoder'] as const;
export type StreamOutputSourceType = typeof STREAM_OUTPUT_SOURCE_TYPES[number];

/** Stream grid column definitions */
export const STREAM_GRID_COLUMNS = [
  { key: 'checkbox', label: '', width: 48, sortable: false, align: 'center' as const },
  { key: 'title', label: 'Title', width: 'auto', sortable: true, align: 'left' as const },
  { key: 'liveStatus', label: 'Live Status', width: 120, sortable: true, align: 'center' as const },
  { key: 'location', label: 'Location', width: 140, sortable: true, align: 'left' as const },
  { key: 'status', label: 'Status', width: 100, sortable: true, align: 'center' as const },
  { key: 'published', label: 'Published', width: 150, sortable: true, align: 'right' as const },
  { key: 'updated', label: 'Updated', width: 150, sortable: true, align: 'right' as const },
  { key: 'actions', label: '', width: 60, sortable: false, align: 'center' as const },
] as const;

/** Default sort: updated desc (most recently modified first) */
export const DEFAULT_STREAM_SORT_KEY: StreamSortKey = 'updated';
export const DEFAULT_STREAM_SORT_DIRECTION: SortDirection = 'desc';

export const STREAM_SORT_KEYS = ['title', 'liveStatus', 'location', 'status', 'published', 'updated'] as const;

/** Live status polling interval (ms) -- 15 seconds matches legacy */
export const LIVE_STATUS_POLL_INTERVAL_MS = 15_000;

/** Stream grid row height (no thumbnail, shorter than video grid) */
export const STREAM_ROW_HEIGHT = 56;

/** Virtualizer overscan */
export const STREAM_VIRTUALIZER_OVERSCAN = 10;

/** Containment guards (same approach as video grid) */
export const STREAM_GRID_MAX_VIRTUAL_ROWS = 72;
export const STREAM_GRID_MAX_VIEWPORT_HEIGHT_PX = 2200;

/** Search debounce */
export const STREAM_SEARCH_DEBOUNCE_MS = 200;

/** Stream field constraints (shared with future form validation) */
export const STREAM_CONSTRAINTS = {
  TITLE_MIN: 1,
  TITLE_MAX: 150,
  DESC_SHORT_MAX: 250,
  OFFLINE_MSG_MAX: 500,
  RECORD_APPEND_TIMEOUT_MIN: 5,
  RECORD_APPEND_TIMEOUT_MAX: 1800,
} as const;

/**
 * Default stream output configuration.
 * Resolution: 540P, Audio: 128 kbps @ 48 kHz.
 */
export const STREAM_DEFAULT_OUTPUT = {
  streamOutputSourceType: 'publisher',
  audioKbps: 128,
  audioSamplerateHz: 48000,
  codec: 'h264_aac',
  videoFps: 30,
  videoKbps: 872,   // 1000 (540P recommended) - 128 audio
  videoWidth: 960,
  videoHeight: 540,
} as const;

/**
 * Default stream create values -- matches legacy VideoStreamView.create().
 */
export const STREAM_CREATE_DEFAULTS = {
  mediaType: 'VideoStream',
  streamType: 'push',
  recordMode: 'manual',
  adaptiveMode: 'none',
  dvr: false,
  dvrArchiveStrategyType: 'append',
  encryptHlsAes: false,
  oosMode: 'offline',
  oosEndMins: null,
  offlineDisplayMode: 'custom_message',
  recordAppendTimeout: 40,
} as const;

/** Live status sort ranking (higher = sorts first in desc) */
export const LIVE_STATUS_RANK = {
  recording: 2,
  live: 1,
  offline: 0,
} as const;

// --- Stream Detail Constants ---

/** Ad injection presets (seconds) */
export const AD_BREAK_DURATIONS = [
  { value: '15', label: '15 seconds' },
  { value: '30', label: '30 seconds' },
  { value: '45', label: '45 seconds' },
  { value: '60', label: '1 minute' },
  { value: '90', label: '1 min 30 sec' },
  { value: '120', label: '2 minutes' },
  { value: '150', label: '2 min 30 sec' },
  { value: '180', label: '3 minutes' },
  { value: '240', label: '4 minutes' },
  { value: '300', label: '5 minutes' },
  { value: 'custom', label: 'Custom' },
] as const;

export const AD_BREAK_MIN_SECONDS = 5;
export const AD_BREAK_MAX_SECONDS = 360;

/** Offline display mode labels (all backend values) */
export const OFFLINE_DISPLAY_MODE_LABELS: Record<StreamOfflineDisplayMode, string> = {
  default_message: 'Default Message',
  custom_message: 'Custom Message',
  offline_poster: 'Offline Poster',
  media: 'Play Video',
  list: 'Play Playlist',
} as const;

/** Editable offline modes in v3 (others shown read-only) */
export const EDITABLE_OFFLINE_MODES: StreamOfflineDisplayMode[] = ['default_message', 'custom_message'];

/** Editable record modes in v3 (schedule shown read-only) */
export const EDITABLE_RECORD_MODES: StreamRecordMode[] = ['manual', 'automatic'];

/** Record mode labels for display */
export const RECORD_MODE_LABELS: Record<StreamRecordMode, string> = {
  manual: 'Manually Record',
  automatic: 'Always Record',
  schedule: 'Schedule',
} as const;

/** Prefetch threshold for stream detail */
export const STREAM_DETAIL_PREFETCH_MAX_ROWS = 2000;

// --- Stream Output Presets ---

/**
 * Resolution presets for stream outputs.
 * recommendedKbps is the total bitrate (video + audio).
 * Matches legacy StreamOutputUtil.resolutions.
 */
export const STREAM_RESOLUTION_PRESETS = [
  { width: 1920, height: 1080, label: '1920x1080 (1080P)', recommendedKbps: 3500 },
  { width: 1280, height: 720, label: '1280x720 (720P)', recommendedKbps: 2000 },
  { width: 960, height: 540, label: '960x540 (540P)', recommendedKbps: 1000 },
  { width: 768, height: 432, label: '768x432 (432P)', recommendedKbps: 800 },
  { width: 640, height: 360, label: '640x360 (360P)', recommendedKbps: 600 },
  { width: 480, height: 270, label: '480x270 (270P)', recommendedKbps: 400 },
  { width: 640, height: 480, label: '640x480 (4:3)', recommendedKbps: 800 },
  { width: 480, height: 360, label: '480x360 (4:3)', recommendedKbps: 500 },
  { width: 320, height: 240, label: '320x240 (4:3)', recommendedKbps: 300 },
] as const;

export type StreamResolutionPreset = typeof STREAM_RESOLUTION_PRESETS[number];

/** Default resolution index: 1080P */
export const STREAM_DEFAULT_RESOLUTION_INDEX = 0;

/** Stream output constraints */
export const STREAM_OUTPUT_CONSTRAINTS = {
  MIN_OUTPUTS: 1,
  MAX_OUTPUTS: 6,
  MIN_BITRATE_KBPS: 150,
  MAX_BITRATE_KBPS: 20000,
} as const;
